import { useState } from 'react';
import { Instagram, Twitter, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { CountdownTimer } from '@/components/CountdownTimer';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import '@/App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set your launch date here
const LAUNCH_DATE = new Date();
LAUNCH_DATE.setDate(LAUNCH_DATE.getDate() + 30); // 30 days from now

export default function ComingSoonPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      toast.success("You're on the list!");
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col relative overflow-hidden">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Background image with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1624816804260-65ea43f9615e?auto=format&fit=crop&w=1920&q=80')`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-12">
        {/* Logo */}
        <div className="mb-8 animate-fadeIn">
          <h1 className="font-['Syne'] text-4xl md:text-6xl font-bold tracking-tight text-center">
            LOSTNFOUND<span className="text-[#FF3B30]">860</span>
          </h1>
        </div>

        {/* Tagline */}
        <p className="text-neutral-400 text-center mb-12 max-w-md animate-slideUp" style={{ animationDelay: '0.1s' }}>
          Premium streetwear for the whole family.<br />
          Something big is coming.
        </p>

        {/* Countdown */}
        <div className="mb-12 animate-slideUp" style={{ animationDelay: '0.2s' }}>
          <p className="text-xs uppercase tracking-widest text-neutral-500 text-center mb-4">
            Launching In
          </p>
          <CountdownTimer targetDate={LAUNCH_DATE.toISOString()} />
        </div>

        {/* Email Signup */}
        <div className="w-full max-w-md animate-slideUp" style={{ animationDelay: '0.3s' }}>
          {subscribed ? (
            <div className="text-center p-6 border border-neutral-800 bg-neutral-900/50">
              <p className="text-[#FF3B30] font-medium mb-2">You're on the list!</p>
              <p className="text-neutral-400 text-sm">
                We'll notify you when we drop.
              </p>
            </div>
          ) : (
            <>
              <p className="text-center text-sm text-neutral-400 mb-4">
                Get early access & exclusive drops
              </p>
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-neutral-900/80 border-neutral-800 rounded-none h-12 text-white placeholder:text-neutral-500"
                  data-testid="coming-soon-email"
                />
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-white text-black hover:bg-[#FF3B30] hover:text-white font-medium uppercase tracking-widest text-xs px-8 h-12 rounded-none transition-all duration-300"
                  data-testid="coming-soon-submit"
                >
                  {loading ? 'Joining...' : 'Notify Me'}
                </Button>
              </form>
            </>
          )}
        </div>

        {/* Social Links */}
        <div className="flex items-center space-x-6 mt-12 animate-slideUp" style={{ animationDelay: '0.4s' }}>
          <a 
            href="https://instagram.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-3 border border-neutral-800 hover:border-white hover:text-[#FF3B30] transition-all duration-300"
            data-testid="social-instagram"
          >
            <Instagram size={20} />
          </a>
          <a 
            href="https://twitter.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-3 border border-neutral-800 hover:border-white hover:text-[#FF3B30] transition-all duration-300"
            data-testid="social-twitter"
          >
            <Twitter size={20} />
          </a>
          <a 
            href="mailto:hello@lostnfound860.com"
            className="p-3 border border-neutral-800 hover:border-white hover:text-[#FF3B30] transition-all duration-300"
            data-testid="social-email"
          >
            <Mail size={20} />
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 py-6 text-center">
        <p className="text-neutral-600 text-xs uppercase tracking-widest">
          © 2024 LostNFound860. All rights reserved.
        </p>
      </div>

      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
}
